import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {SafeParseReturnType} from 'zod';
import InputWithCharacterCounter from '../../../../components/slides/highlighted/common/InputWithCharacterCounter';
import {presNameValidator} from '../../../../incase-domain/validators/presentation/name';
import useUpdatePresentation from '../../../../service/hooks/useUpdatePresentation';
import {selectedPresentationMetaSelector} from '../../../../store/selectors/presentation.selectors';
import {composeValidationMessage} from '../../../../utils/validation';

interface PresentationNameInputProps {
    className?: string;
}

function PresentationNameInput({className}: PresentationNameInputProps): JSX.Element {
    const {t} = useTranslation();
    const {mutate: updatePresentation} = useUpdatePresentation();
    const selectedPresentationMeta = useSelector(selectedPresentationMetaSelector);

    const [presentationName, setPresentationName] = useState<{
        name: string;
        validation?: SafeParseReturnType<string, string>;
    }>({name: selectedPresentationMeta.name || ''});

    useEffect(() => {
        if (selectedPresentationMeta.name) {
            setPresentationName((curr) => ({...curr, name: selectedPresentationMeta.name}));
        }
    }, [selectedPresentationMeta.name]);

    const handleChange = useCallback((value: string) => setPresentationName((curr) => ({...curr, name: value})), []);

    const handleBlur = useCallback(
        (value: string): void => {
            const savedNameTrimmed = selectedPresentationMeta.name?.trim();
            const newNameTrimmed = value.trim();

            if (savedNameTrimmed === newNameTrimmed) return;

            const validation = presNameValidator.safeParse(newNameTrimmed);
            setPresentationName((curr) => ({...curr, validation}));

            if (validation.success) {
                updatePresentation({
                    presentation: {...selectedPresentationMeta, name: newNameTrimmed},
                });
            } else {
                validation.error.errors.forEach((error) => toast.error(composeValidationMessage(error.message)));
            }
        },
        [selectedPresentationMeta, updatePresentation]
    );

    return (
        <InputWithCharacterCounter
            className={className}
            value={presentationName.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t('presEditPage.header.presentationTitleInputPlaceholder')}
            enableSubmitByEnter
        />
    );
}

export default React.memo(PresentationNameInput);
